import { SEOHead } from '@/components/seo-head'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Search,
  Building2,
  Shield,
  Users,
  Star,
  ArrowRight,
  Globe,
  FileText,
  MessageSquare,
  TrendingUp,
  Award,
  Clock,
  Zap,
  CheckCircle,
  XCircle,
  DollarSign,
  Target,
  BarChart3
} from 'lucide-react'

export default function ThomasPageAlternative() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SourcedIn - ThomasPage Alternative",
    "description": "Professional B2B marketplace for finding suppliers and vendors. Advanced alternative to ThomasPage with AI-powered search, verified suppliers, and comprehensive business solutions.",
    "url": "https://sourcedin.ai/thomaspage-alternative",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5"
    },
    "author": {
      "@type": "Organization",
      "name": "SourcedIn",
      "url": "https://sourcedin.ai"
    }
  }

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What makes SourcedIn better than ThomasPage?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SourcedIn offers AI-powered search, real-time supplier verification, advanced filtering, and modern user interface compared to ThomasPage's outdated platform."
        }
      },
      {
        "@type": "Question",
        "name": "Is SourcedIn free to use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, SourcedIn offers a free tier with basic features. Premium plans start at $99/month for advanced features and unlimited searches."
        }
      },
      {
        "@type": "Question",
        "name": "How does SourcedIn verify suppliers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SourcedIn uses automated verification, third-party certifications, customer reviews, and direct verification processes to ensure supplier quality and reliability."
        }
      }
    ]
  }

  return (
    <>
      <SEOHead
        title="Best ThomasPage Alternative 2024 | SourcedIn - AI-Powered B2B Marketplace"
        description="Discover the best ThomasPage alternative with SourcedIn. AI-powered supplier search, verified vendors, and advanced B2B marketplace features. Free trial available."
        keywords="thomaspage alternative, thomasnet alternative, b2b marketplace, supplier directory, vendor search, industrial suppliers, manufacturing suppliers, verified suppliers"
        canonical="https://sourcedin.ai/thomaspage-alternative"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4 mr-2" />
                #1 ThomasPage Alternative 2024
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                The Best <span className="text-primary">ThomasPage Alternative</span> for Modern B2B Sourcing
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                Stop struggling with ThomasPage's outdated interface. SourcedIn delivers AI-powered supplier search, 
                real-time verification, and modern tools that actually work for today's procurement professionals.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg">
                  Try SourcedIn Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="px-8 py-4 text-lg">
                  Watch Demo
                </Button>
              </div>

              <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Free 14-day trial
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Setup in 5 minutes
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ThomasPage vs SourcedIn: Why Make the Switch?
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                See why thousands of procurement professionals are switching from ThomasPage to SourcedIn
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-700">
                    <XCircle className="w-5 h-5 mr-2" />
                    ThomasPage Problems
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Outdated, confusing user interface from the 1990s</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Slow search results and poor filtering options</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>No real-time supplier verification or status updates</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Expensive pricing with limited features</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Poor mobile experience and outdated technology</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    SourcedIn Solutions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Modern, intuitive interface designed for 2024</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>AI-powered search with instant, accurate results</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Real-time supplier verification and status tracking</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Transparent pricing with powerful free tier</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Mobile-first design with cutting-edge technology</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Advanced Features That ThomasPage Can't Match
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Built for modern procurement teams who need results, not frustration
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Search className="w-6 h-6 text-blue-600" />
                  </div>
                  <CardTitle>AI-Powered Search</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Find suppliers 10x faster with intelligent search that understands your requirements 
                    and suggests the best matches automatically.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Real-Time Verification</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Every supplier is verified in real-time with certifications, compliance status, 
                    and business health indicators.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <CardTitle>Advanced Analytics</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Track your sourcing performance with detailed analytics, supplier insights, 
                    and market intelligence reports.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <MessageSquare className="w-6 h-6 text-orange-600" />
                  </div>
                  <CardTitle>Direct Communication</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Connect directly with suppliers through our built-in messaging system. 
                    No more playing phone tag or waiting for email responses.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  <CardTitle>RFP Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Create, distribute, and track RFPs with our comprehensive management system. 
                    Compare responses side-by-side with ease.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <CardTitle>Team Collaboration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Work together with your team on supplier evaluations, share notes, 
                    and maintain a centralized knowledge base.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Trusted by Leading Companies Across Industries
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                From Fortune 500 companies to growing startups, see why teams are switching from ThomasPage
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Manufacturing</h3>
                <p className="text-gray-600 text-sm">
                  Find reliable suppliers for production equipment, raw materials, and manufacturing services.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Technology</h3>
                <p className="text-gray-600 text-sm">
                  Source IT services, software solutions, and technology infrastructure providers.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Healthcare</h3>
                <p className="text-gray-600 text-sm">
                  Connect with certified medical suppliers, equipment vendors, and healthcare services.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Construction</h3>
                <p className="text-gray-600 text-sm">
                  Find construction materials, equipment rental, and specialized trade services.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why Choose SourcedIn Over ThomasPage?
              </h2>
              <p className="text-xl opacity-90 max-w-2xl mx-auto">
                Join thousands of procurement professionals who've made the switch
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">10x Faster Search</h3>
                <p className="opacity-90">
                  Find suppliers in seconds, not minutes. Our AI-powered search delivers 
                  relevant results instantly.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Save 30% on Costs</h3>
                <p className="opacity-90">
                  Better supplier selection and negotiation tools help you find 
                  better deals and reduce procurement costs.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Higher Quality Suppliers</h3>
                <p className="opacity-90">
                  Real-time verification ensures you only work with reliable, 
                  certified suppliers who deliver on their promises.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Our Customers Say About Switching from ThomasPage
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Real feedback from procurement professionals who made the switch
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-4">
                    "Switching from ThomasPage to SourcedIn was the best decision we made. 
                    The search is so much faster and the interface actually makes sense. 
                    We've reduced our supplier search time by 80%."
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold">SM</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Sarah Mitchell</p>
                      <p className="text-sm text-gray-500">Procurement Manager, TechCorp</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-4">
                    "ThomasPage felt like it was stuck in the past. SourcedIn has everything 
                    we need in a modern, intuitive package. The supplier verification 
                    feature alone has saved us countless hours."
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-green-600 font-semibold">MJ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Michael Johnson</p>
                      <p className="text-sm text-gray-500">Supply Chain Director, Manufacturing Inc</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-4">
                    "The AI-powered search is incredible. It actually understands what we're 
                    looking for and suggests suppliers we never would have found on ThomasPage. 
                    Game-changing technology."
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-purple-600 font-semibold">LD</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Lisa Davis</p>
                      <p className="text-sm text-gray-500">VP of Operations, HealthTech Solutions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about switching from ThomasPage to SourcedIn
              </p>
            </div>

            <div className="space-y-8">
              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  What makes SourcedIn better than ThomasPage?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  SourcedIn offers a modern, intuitive interface with AI-powered search capabilities, 
                  real-time supplier verification, advanced filtering options, and comprehensive 
                  analytics. Unlike ThomasPage's outdated platform, SourcedIn is built for today's 
                  procurement professionals who need speed, accuracy, and results.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Is SourcedIn free to use?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! SourcedIn offers a generous free tier that includes basic search functionality, 
                  supplier profiles, and essential features. Premium plans start at $99/month and 
                  include advanced analytics, unlimited searches, priority support, and team collaboration tools.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  How does SourcedIn verify suppliers?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  SourcedIn uses a comprehensive verification process including automated business 
                  checks, third-party certifications, compliance monitoring, customer reviews, 
                  and direct verification with suppliers. All verification data is updated in real-time 
                  to ensure accuracy.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Can I migrate my data from ThomasPage?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  While we can't directly import data from ThomasPage due to their platform limitations, 
                  our team can help you set up your supplier database and import your existing supplier 
                  information. We offer free migration assistance for new customers.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Does SourcedIn have mobile access?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Absolutely! SourcedIn is built mobile-first with responsive design that works 
                  perfectly on smartphones and tablets. You can search suppliers, view profiles, 
                  and manage RFPs from anywhere, unlike ThomasPage's desktop-only experience.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  How quickly can I get started?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  You can start using SourcedIn immediately after signing up. Our intuitive interface 
                  means you'll be finding suppliers within minutes, not hours. Most users report 
                  being fully productive within their first session.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-green-600 text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Leave ThomasPage Behind?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of procurement professionals who've already made the switch. 
              Start your free trial today and experience the difference.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" className="bg-white text-primary hover:bg-gray-100 px-8 py-4 text-lg">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 py-4 text-lg">
                Schedule Demo
              </Button>
            </div>

            <div className="flex items-center justify-center space-x-8 text-sm opacity-80">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Free 14-day trial
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                No setup fees
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Cancel anytime
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
